'use client'

import NextLink from '@/components/NextLink';
import { useAuthenticationModalStore } from '@/core/stores/auth';
import { useItineraryStoreV2 } from '@/core/stores/itinerary/itineraryV2';
import { useItinerarySettingsStore } from '@/core/stores/itinerary/settings';
import { useUserStore } from '@/core/stores/user';
import { generateAvatar } from '@/utils/generateAvatar';
import { faArrowRightToBracket } from "@fortawesome/pro-duotone-svg-icons/faArrowRightToBracket";
import { faBed } from "@fortawesome/pro-regular-svg-icons/faBed";
import { faBookOpenCover } from "@fortawesome/pro-regular-svg-icons/faBookOpenCover";
import { faClockRotateLeft } from "@fortawesome/pro-regular-svg-icons/faClockRotateLeft";
import { faGear } from "@fortawesome/pro-regular-svg-icons/faGear";
import { faLandmarkMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons/faLandmarkMagnifyingGlass";
import { faMessages } from "@fortawesome/pro-regular-svg-icons/faMessages";
import { faPlus } from "@fortawesome/pro-regular-svg-icons/faPlus";
import { faUserPlus } from "@fortawesome/pro-regular-svg-icons/faUserPlus";
import { faBed as faBedSolid } from "@fortawesome/pro-solid-svg-icons/faBed";
import { faBookOpenCover as faBookOpenCoverSolid } from "@fortawesome/pro-solid-svg-icons/faBookOpenCover";
import { faClockRotateLeft as faClockRotateLeftSolid } from "@fortawesome/pro-solid-svg-icons/faClockRotateLeft";
import { faLandmarkMagnifyingGlass as faLandmarkMagnifyingGlassSolid } from "@fortawesome/pro-solid-svg-icons/faLandmarkMagnifyingGlass";
import { faPlus as faPlusSolid } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { ReactNode } from 'react';
import AccessController from './AccessController';
import dynamic from 'next/dynamic';
import { useItineraryMapStore } from '@/core/stores/itinerary';
import ImageLoading from '@/components/ImageLoading';

const ShareItineraryModal = dynamic(() => import('./shareItineraryModal'))
const ItinerarySettingsModal = dynamic(() => import('@/app/itineraries/(map-layout)/components/itinerarySettingsModal'))

export default function SidebarMenu({ id, baseApi, hasUserToken }: { id: string, baseApi: string, hasUserToken: boolean }) {
    const pathname = usePathname();
    const { setShowSettingsModal, setShowShareModal, setShowSidebar,
        setShowGroupChat, showSettingsModal, showShareModal } =
        useItinerarySettingsStore()
    const { itinerary, baseUrl: otherBaseUrl, } = useItineraryStoreV2()
    const { setModalData } = useItineraryMapStore()
    const itineraryId = itinerary?.id
    const startedAt = itinerary?.startedAt
    const endedAt = itinerary?.endedAt

    const { setShowAuthModal } = useAuthenticationModalStore()
    const { _id, image, email } = useUserStore()
    const baseUrl = `/itineraries`;


    return (
        <>
            <ItinerarySettingsModal isOpen={showSettingsModal} onClose={() => setShowSettingsModal(false)} />
            <div className="border-r z-50 max-h-dvh appHeight overflow-y-auto no-scroller hidden w-20 flex-col justify-between bg-white p-3 relative md:flex">
                <div>
                    <NextLink
                        href={"/"}
                        className="mb-8 flex  relative aspect-square items-center justify-center  text-3xl text-white"
                    >
                        <Image className='rounded' width={300} height={300} alt='planingo' src={`${process.env.NEXT_PUBLIC_STORAGE_URL}assets/294831b9-b06f-49c3-b532-755dd9b2ab98-planingo.png`} />
                    </NextLink>

                    {pathname !== '/itineraries' && pathname !== '/itineraries/generate' && itineraryId &&
                        <NavLink
                            href={`${otherBaseUrl}`}
                            icon={pathname === otherBaseUrl || pathname.includes(`${otherBaseUrl}/days`) ?
                                <FontAwesomeIcon icon={faBookOpenCoverSolid} size="2xl" className='text-indigo-500 w-5 h-5' /> :
                                <FontAwesomeIcon icon={faBookOpenCover} size="2xl" className='text-gray-700/80 w-5 h-5' />}
                            isActive={pathname === `${otherBaseUrl}` || pathname.includes(`${otherBaseUrl}/days`)}
                            label="Itinerary"
                        />}

                    {pathname !== '/itineraries' && itineraryId && pathname !== '/itineraries/generate' &&
                        <div className="flex flex-col gap-2">

                            <NavLink
                                href={`${otherBaseUrl}/explore`}
                                icon={pathname === `${otherBaseUrl}/explore` ?
                                    <FontAwesomeIcon icon={faLandmarkMagnifyingGlassSolid} size="2xl" className='text-indigo-500 w-5 h-5' /> :
                                    <FontAwesomeIcon icon={faLandmarkMagnifyingGlass} size="2xl" className='text-gray-700/80 w-5 h-5' />}
                                isActive={pathname === `${otherBaseUrl}/explore`}
                                label="Explore"
                            />
                            <NavLink
                                href={`${otherBaseUrl}/hotels`}
                                icon={pathname === `${otherBaseUrl}/hotels` ?
                                    <FontAwesomeIcon icon={faBedSolid} size="2xl" className='text-indigo-500 w-5 h-5' /> :
                                    <FontAwesomeIcon icon={faBed} size="2xl" className='text-gray-700/80 w-5 h-5' />}
                                isActive={pathname === `${otherBaseUrl}/hotels?${startedAt && endedAt ? `std=${startedAt}&end=${endedAt}` : ''}`}
                                label="Hotels"
                            />
                            <button onClick={() => {
                                if (hasUserToken) {
                                    setShowGroupChat(true)
                                    setModalData(null)
                                } else {
                                    setShowAuthModal(true)
                                }

                            }} className={`text-gray-700 flex flex-col items-center gap-2 rounded-xl  py-2 text-xs `} >
                                <FontAwesomeIcon icon={faMessages} size="2xl" className='text-gray-700/80 w-5 h-5' />
                                <span className={`text-gray-400 text-center min-w-max text-xs`}> Group Chat </span>
                            </button>
                        </div>
                    }
                    <div className='w-full border-neutral-300 border-t pt-1.5 mt-1.5'>
                        <NavLink
                            href={`/itineraries/my-trips`}
                            onClick={() => setShowSidebar(false)}
                            icon={pathname === `/itineraries/my-trips` ?
                                <FontAwesomeIcon icon={faClockRotateLeftSolid} size="xl" className='text-indigo-500 w-5 h-5' /> :
                                <FontAwesomeIcon icon={faClockRotateLeft} size="xl" className='text-gray-700/80 w-5 h-5' />}
                            isActive={pathname === `/itineraries/my-trips`}
                            label="My Trips"
                        />
                    </div>
                    {/* <NavLink
                        href={`/itineraries`}
                        icon={pathname === baseUrl ?
                            <FontAwesomeIcon icon={faPlusSolid} size="xl" className='text-indigo-500 w-5 h-5' /> :
                            <FontAwesomeIcon icon={faPlus} size="xl" className='text-gray-700/80 w-5 h-5' />}
                        isActive={pathname === baseUrl}
                        label="New"
                    /> */}
                </div>
                {showShareModal && <ShareItineraryModal baseApi={baseApi} isOpen={showShareModal} onClose={() => setShowShareModal(false)} />}
                <div className="flex flex-col gap-2">
                    {pathname !== '/itineraries' && pathname !== '/itineraries/generate' && itineraryId && <div className="flex flex-col gap-2">
                        {/* <NavLink
                        href={`${baseUrl}/tripmates`}
                        onClick={() => setShowSidebar(false)}
                        icon={<Tripmate className="w-6 text-gray-400" />}
                        isActive={pathname === `${baseUrl}/tripmates`}
                        label="Tripmates"
                    /> */}
                        <AccessController allowedRoles={['owner', 'editor']}>
                            <button
                                onClick={() => {
                                    setShowShareModal(true)
                                }}
                                className={`  
                                     flex flex-col items-center gap-2 rounded-xl  py-2 text-xs text-gray-700/80`}
                            >
                                <FontAwesomeIcon icon={faUserPlus} size="2xl" className='text-gray-700/80 w-5 h-5' />
                                <span className={` text-gray-400`}>
                                    Tripmates
                                </span>
                            </button>
                        </AccessController>
                        <AccessController allowedRoles={['owner', 'editor']}>
                            {<button
                                onClick={() => {
                                    setShowSettingsModal(!showSettingsModal)
                                }}
                                className={`flex flex-col  items-center gap-2 rounded-xl  py-2 text-xs `}
                            >
                                <FontAwesomeIcon icon={faGear} size="2xl" className="w-5 text-gray-700/80 h-5" />
                                <span className={`text-gray-400`}>
                                    Settings
                                </span>
                            </button>}
                        </AccessController>
                    </div>}
                    {_id ? <NavLink
                        href={`/profile`}
                        icon={
                            <div className='w-6 h-6 rounded-full overflow-hidden object-cover bg-slate-50'>
                                <ImageLoading alt="Planingo"
                                    className='w-6 h-6 rounded-full overflow-hidden object-cover bg-slate-50'
                                    width={100} height={100} src={image || generateAvatar(email)} />
                            </div>
                        }
                        isActive={pathname === `/profile`}
                        label="Profile"
                    /> :
                        <button onClick={() => setShowAuthModal(true)} className='flex items-center flex-col gap-2'>
                            <FontAwesomeIcon className='text-gray-700/80' icon={faArrowRightToBracket} />
                            <span className='text-gray-500 text-xs '>Log in</span>
                        </button>
                    }
                </div>

            </div >

        </>

    )
}


const NavLink = ({ isActive, label, href, icon, onClick }: { isActive: boolean; label: string; href: string; icon: ReactNode; onClick?: () => void }) => {
    return (
        <NextLink
            href={href}
            onClick={onClick}
            className={`${isActive ? "bg-indigo-50 text-indigo-500" : "text-gray-700 "} 
                    flex flex-col items-center gap-2 rounded-xl  py-2 text-xs `}
        >
            {icon}
            <span className={`${isActive ? "text-indigo-500" : "text-gray-400"} text-center`}>
                {label}
            </span>
        </NextLink>
    );
};
